import React, { FunctionComponent } from 'react';
import {
  Grid,
  Typography,
  makeStyles,
  IconButton,
  Snackbar,
  UnfoldMoreOutlinedIcon,
  UnfoldLessOutlinedIcon,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { SvgIconCopy } from '@modules/ui/icons/EngineReferenceIcons';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import { getEngineRevampHeadingStyle } from '@modules/ui/shared/utils/styles';
import CodeBlockCore from './CodeBlockCore';
import useCodeBlockState from '../hooks/useCodeBlockState';
import useCodeBlockContent from '../hooks/useCodeBlockContent';

const useCodeBlockV2Styles = makeStyles()((theme) => ({
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  codeSampleTitle: {
    ...getEngineRevampHeadingStyle(),
  },
  descriptionContainer: {
    marginTop: '8px',
    marginBottom: '8px',
  },
  languageLabel: {
    ...theme.typography.caption,
    color: theme.palette.content.muted,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  container: {
    position: 'relative',
    border: `1px solid ${theme.palette.components.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    // Override line number column background
    '& span': {
      background: 'transparent !important',
    },
    // Override SyntaxHighlighter background
    '& pre': {
      background: 'transparent !important',
    },
    '& code': {
      background: 'transparent !important',
    },
  },
  copyButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
    backgroundColor: theme.palette.surface[0],
    '&:hover': {
      backgroundColor: theme.palette.surface[0],
    },
  },
  expandButton: {
    position: 'absolute',
    top: 8,
    right: 40, // 8px (copy button spacing) + 24px (icon size) + 8px (additional padding)
    backgroundColor: theme.palette.surface[0],
    zIndex: 1,
    '&:hover': {
      backgroundColor: theme.palette.surface[0],
    },
  },
}));

export interface CodeBlockV2Props {
  content: string;
  language?: string;
  title?: string;
  description?: string;
  maxContentHeight?: number;
  enableCopy?: boolean;
  enableExpand?: boolean;
  showLanguageLabel?: boolean;
  className?: string;
}

/**
 * Modern code block without Card wrapper (V2).
 * Features floating copy/expand buttons with optional title, language label, and description.
 *
 * Use this for the engine reference revamp.
 */
const CodeBlockV2: FunctionComponent<CodeBlockV2Props> = ({
  content,
  language = 'luau',
  title,
  description,
  maxContentHeight = 400,
  enableCopy = true,
  enableExpand = true,
  showLanguageLabel = true,
  className,
}) => {
  const { classes, cx } = useCodeBlockV2Styles();
  const {
    isContentExpanded,
    showSnackbar,
    handleExpandContent,
    handleCopyToClipboard,
    setShowSnackbar,
  } = useCodeBlockState();
  const { exceedsMaxHeight } = useCodeBlockContent(content, maxContentHeight);
  const { translate } = useTranslation();
  const snackbarText = translate('Label.CodeSnippetCopyMessage') || 'Copied to clipboard';

  return (
    <Grid>
      {(title || showLanguageLabel) && (
        <div className={classes.titleContainer}>
          <Typography variant='subtitle2' className={classes.codeSampleTitle}>
            {title}
          </Typography>
          {showLanguageLabel && (
            <Typography className={classes.languageLabel}>{language}</Typography>
          )}
        </div>
      )}
      {description && (
        <div className={classes.descriptionContainer}>
          <EngineMarkdown>{description}</EngineMarkdown>
        </div>
      )}
      <div className={cx(classes.container, className)}>
        {enableExpand && exceedsMaxHeight && (
          <IconButton
            className={classes.expandButton}
            aria-label={
              isContentExpanded
                ? translate('Label.CollapseCodeBlock') || 'Collapse code block'
                : translate('Label.ExpandCodeBlock') || 'Expand code block'
            }
            color='default'
            size='small'
            onClick={handleExpandContent}>
            {isContentExpanded ? (
              <UnfoldLessOutlinedIcon fontSize='small' />
            ) : (
              <UnfoldMoreOutlinedIcon fontSize='small' />
            )}
          </IconButton>
        )}
        {enableCopy && (
          <IconButton
            className={classes.copyButton}
            aria-label={translate('Label.CopyCodeToClipboard') || 'Copy code to clipboard'}
            color='default'
            size='small'
            onClick={() => handleCopyToClipboard(content)}>
            <SvgIconCopy />
          </IconButton>
        )}
        <CodeBlockCore
          content={content}
          language={language}
          isExpanded={isContentExpanded}
          maxContentHeight={maxContentHeight}
        />
      </div>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message={snackbarText}
        autoHideDuration={3000}
      />
    </Grid>
  );
};

export default CodeBlockV2;
